import * as PIXI from "pixi.js";
import Reconciler, { HostConfig } from "react-reconciler";
import { DefaultEventPriority } from "react-reconciler/constants";

type Instance = PIXI.Graphics | PIXI.Container;

type BoxProps = any;

type Props = BoxProps;

const EMPTY_OBJECT = {};

const hostConfig: HostConfig<
  string, // Type
  Props, // Props
  PIXI.Application, // Container
  Instance, // Instance
  Instance, // TextInstance
  Instance, // SuspenseInstance
  Instance, // HydratableInstance
  Instance, // PublicInstance
  unknown, // HostContext
  unknown, // UpdatePayload
  unknown, // ChildSet,
  unknown, // TimeoutHandle
  unknown // NoTimeout
> = {
  getCurrentEventPriority: () => {
    return DefaultEventPriority;
  },
  getRootHostContext() {
    return EMPTY_OBJECT;
  },

  getChildHostContext() {
    return EMPTY_OBJECT;
  },

  getPublicInstance(instance) {
    return instance;
  },

  prepareForCommit() {
    return null;
  },

  resetAfterCommit() {},

  createInstance: (
    type,
    props,
    rootContainer,
    _hostContext,
    _internalHandle
  ) => {
    switch (type) {
      case "container": {
        const group = new PIXI.Container();
        group.x = props.x;
        group.y = props.y;
        group.rotation = props.rotation;
        return group;
      }
      case "box": {
        const rectangleGraphics = new PIXI.Graphics();
        rectangleGraphics.beginFill(0xffffff, 1);
        rectangleGraphics.drawRect(0, 0, 1, 1);
        rectangleGraphics.x = props.x;
        rectangleGraphics.y = props.y;
        rectangleGraphics.scale.x = props.width;
        rectangleGraphics.scale.y = props.height;
        rectangleGraphics.rotation = props.rotation;
        rectangleGraphics.tint = props.fillColor;
        rectangleGraphics.pivot.x = props.pivotX;
        rectangleGraphics.pivot.y = props.pivotY;
        rectangleGraphics.alpha = props.opacity;
        return rectangleGraphics;
      }
      default: {
        throw new Error(`Unknown node type ${type}`);
      }
    }
  },

  hideInstance(instance) {
    instance.visible = false;
  },

  unhideInstance(instance, props) {
    instance.visible = true;
  },

  finalizeInitialChildren(wordElement, type, props) {
    return false;
  },

  prepareUpdate(
    instance,
    type,
    oldProps,
    newProps,
    rootContainer,
    hostContext
  ) {
    return EMPTY_OBJECT;
  },

  shouldSetTextContent() {
    return false;
  },

  createTextInstance() {
    throw new Error("react-graphics renderer does not support text nodes");
  },

  unhideTextInstance() {},

  scheduleTimeout: setTimeout,

  cancelTimeout: clearTimeout,

  noTimeout: -1,

  warnsIfNotActing: false,

  isPrimaryRenderer: false,

  supportsMutation: true,

  supportsPersistence: false,

  supportsHydration: false,

  supportsMicrotask: true,

  appendInitialChild(parentInstance, child) {
    parentInstance.addChild(child);
  },

  appendChild(parentInstance, child) {
    parentInstance.addChild(child);
  },

  appendChildToContainer(container, child) {
    container.stage.addChild(child);
  },

  removeChild(parentInstance, child) {
    child.destroy();
  },

  removeChildFromContainer(container, child) {
    child.destroy();
  },

  insertBefore: (parentInstance, child, beforeChild) => {
    parentInstance.addChildAt(
      child,
      parentInstance.children.findIndex((c) => c === beforeChild)
    );
  },

  insertInContainerBefore(container, child, beforeChild) {
    container.stage.addChildAt(
      child,
      container.stage.children.findIndex((c) => c === beforeChild)
    );
  },

  commitUpdate(instance, updatePayload, type, oldProps, newProps) {
    switch (type) {
      case "container": {
        instance.x = newProps.x;
        instance.y = newProps.y;
        instance.rotation = newProps.rotation;
        break;
      }
      case "box": {
        instance.x = newProps.x;
        instance.y = newProps.y;
        instance.scale.x = newProps.width;
        instance.scale.y = newProps.height;
        instance.rotation = newProps.rotation;
        instance.pivot.x = newProps.pivotX;
        instance.pivot.y = newProps.pivotY;
        instance.alpha = newProps.opacity;
        (instance as PIXI.Graphics).tint = newProps.fillColor;
        break;
      }
      default: {
        throw new Error(`Unknown element type ${type}`);
      }
    }
  },

  commitMount() {},

  commitTextUpdate() {},

  resetTextContent() {},

  clearContainer(container) {
    // TODO
  },

  getInstanceFromNode(node) {
    throw new Error("Not yet implemented.");
  },

  afterActiveInstanceBlur() {
    // noop
  },

  preparePortalMount(portalInstance) {
    // noop
  },

  detachDeletedInstance: () => {
    // noop
  },

  beforeActiveInstanceBlur() {},

  prepareScopeUpdate() {},

  getInstanceFromScope() {
    return null;
  },
};

const reconciler = Reconciler(hostConfig);

export default reconciler;
